import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import { paths } from '../../../utils/constants/routes';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { FUNDS_VIEW_MODE, FUNDS_VIEW_TABS, FUNDS_VIEW_ANALYTICS_TABS_EVENTS } from '../constants';
import Input from '../../../components/inputs/Input';
import {
  FUND_FIELDS,
  getErrorFields,
  compareFields,
  trimStateFields
} from '../../../utils/constants/fields';
import TextArea from '../../../components/inputs/TextArea';
import { runInAction } from 'mobx';
import MultiSelect from '../../../components/inputs/MultiSelect';
import RangeInput from '../../../components/inputs/RangeInput';
import { mapFieldsToState } from '../../../utils/utils';
import useHistory from '../../../hooks/useHistory';
import { eventPageviewFundCreate, eventFundCreationStep } from '../../../ga4/ga4';
import { mapData } from '../../../api/dataMappers';
import { GA_EVENTS } from '../../../ga4/constants';

const FundView = observer(({ mode = FUNDS_VIEW_MODE.CREATE, fund = null }) => {
  const { fundsStore, utilsStore } = useStore();
  const { goBack } = useHistory();
  const state = useLocalObservable(() => ({
    fundId: '',
    setFundId: (id = '') => (state.fundId = id),
    fields: {
      ...mapFieldsToState(FUND_FIELDS),
      [FUND_FIELDS.NAME.NAME]:
        mode === FUNDS_VIEW_MODE.CREATE ? `Fund ${fundsStore.funds.length + 1}` : ''
    },
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    prevTab: null,
    tab: FUNDS_VIEW_TABS.INFORMATION,
    setTab: (value = '', id = '') => {
      [state.tab, state.prevTab] = [value, state.tab];
      const tabDOM = document.getElementById(id);
      if (tabDOM) {
        setTimeout(() => {
          tabDOM.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 0);
      }
    },
    onSubmitErrorState: false,
    setOnSubmitErrorState: (value = false) => (state.onSubmitErrorState = value),
    get validationFields() {
      return getErrorFields(Object.values(FUND_FIELDS), state.fields, [FUND_FIELDS.VINTAGE]);
    },
    get isRestoreDisabled() {
      const hasDiff =
        mode === FUNDS_VIEW_MODE.CREATE
          ? !!Object.entries(state.fields).find(([fieldName, fieldValue]) => {
              if (fieldName === FUND_FIELDS.NAME.NAME) {
                return fieldValue !== `Fund ${fundsStore.funds.length + 1}`;
              }
              return Array.isArray(fieldValue) ? !!fieldValue.length : fieldValue !== '';
            })
          : compareFields(state.fields, fund);

      return (
        !hasDiff ||
        (mode === FUNDS_VIEW_MODE.CREATE && fundsStore.isCreatingFund) ||
        (mode === FUNDS_VIEW_MODE.EDIT && fundsStore.fundsInAction[fund.id])
      );
    },
    get isSaveDisabled() {
      return (
        state.isRestoreDisabled ||
        state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length ||
        (state.onSubmitErrorState && state.validationFields.invalidFields.length)
      );
    },
    restore: () => {
      if (mode === FUNDS_VIEW_MODE.CREATE) {
        state.fields = {
          ...mapFieldsToState(FUND_FIELDS),
          [FUND_FIELDS.NAME.NAME]:
            mode === FUNDS_VIEW_MODE.CREATE ? `Fund ${fundsStore.funds.length + 1}` : ''
        };
      } else {
        const initial = mapFieldsToState(FUND_FIELDS);
        Object.entries(initial).forEach(([fieldName, initialFieldValue]) => {
          state.fields[fieldName] = fund[fieldName] || initialFieldValue;
        });
      }
    },
    getAnalyticsFormData: () => {
      const data = mapData(state.fields, FUND_FIELDS, true);
      return Object.fromEntries(
        Object.values(FUND_FIELDS).map((fieldConfig) => {
          const fieldName = fieldConfig.NAME;
          const fieldValue = fieldConfig.VALUES_KEY
            ? utilsStore.getOptionName(data[fieldName], fieldConfig)
            : data[fieldName];
          return [fieldName, fieldValue];
        })
      );
    }
  }));

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const tabTitle = {
      [FUNDS_VIEW_MODE.CREATE]: 'Create',
      [FUNDS_VIEW_MODE.EDIT]: 'Edit'
    }[mode];
    document.title = `transact.digital GmbH - Fund (${tabTitle})`;
  }, [mode]);

  useEffect(() => {
    if (mode === FUNDS_VIEW_MODE.CREATE) {
      eventPageviewFundCreate();
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== FUNDS_VIEW_MODE.CREATE || !state.prevTab) {
      return;
    }

    eventFundCreationStep(
      FUNDS_VIEW_ANALYTICS_TABS_EVENTS[state.prevTab],
      state.getAnalyticsFormData()
    );
  }, [mode, state, state.prevTab]);

  useEffect(() => {
    if (mode === FUNDS_VIEW_MODE.CREATE) {
      return;
    }
    if (fund.id === state.fundId) {
      return;
    }

    if (mode === FUNDS_VIEW_MODE.EDIT && fund) {
      const initial = mapFieldsToState(FUND_FIELDS);
      runInAction(() => {
        Object.keys(state.fields).forEach((fieldName) => {
          state.fields[fieldName] = fund[fieldName] || initial[fieldName];
        });
      });
    }
    state.setFundId(fund.id);
  }, [state, mode, fund]);

  const tabConfig = [
    { path: FUNDS_VIEW_TABS.INFORMATION, desktopLabel: 'Fund information', id: 'FV_INF_TAB' },
    { path: FUNDS_VIEW_TABS.GEO_INDUSTRIES, desktopLabel: 'Geo & Industries', id: 'FV_GEO_TAB' },
    { path: FUNDS_VIEW_TABS.FINANCIAL_METRICS, desktopLabel: 'Financials', id: 'FV_FIN_TAB' },
    { path: FUNDS_VIEW_TABS.TRANSACTION_TYPES, desktopLabel: 'Transaction', id: 'FV_TRA_TAB' },
    { path: FUNDS_VIEW_TABS.VALUATION, desktopLabel: 'Valuation', id: 'FV_VAL_TAB' }
  ];

  const goToFirstErroredTab = () => {
    setTimeout(() => {
      const firstErroredTabDOM = document.querySelector('div.tabs > div.tab.error-tab');
      const tab = tabConfig.find((t) => t.id === firstErroredTabDOM?.id);
      if (!tab || tab.path === state.tab) {
        return;
      }

      state.setTab(tab.path, tab.id);
    }, 10);
  };

  const createFund = () => {
    eventFundCreationStep(
      FUNDS_VIEW_ANALYTICS_TABS_EVENTS[state.tab],
      state.getAnalyticsFormData()
    );

    trimStateFields(state.fields);
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      goToFirstErroredTab();
      return;
    }

    fundsStore.createFund(state.fields, () => {
      eventFundCreationStep(GA_EVENTS.fund_create_complete, state.getAnalyticsFormData());
      goBack(paths.FUNDS);
    });
  };

  const editFund = () => {
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      goToFirstErroredTab();
      return;
    }

    fundsStore.editFund(fund, state.fields, () => {
      goBack(paths.FUNDS);
    });
  };

  const onBackClick = (e) => {
    e?.preventDefault?.();
    goBack(paths.FUNDS);
  };

  const erroredFields = Object.fromEntries(
    state.validationFields.invalidFields.map(({ name }) => [name, true])
  );

  const tabsInfo = [];
  tabConfig.forEach(({ path }) => {
    const tabInfo = { hasErrorField: false };
    tabsInfo.push(tabInfo);

    if (!state.onSubmitErrorState) {
      return;
    }

    const fields = Object.values(FUND_FIELDS).filter((f) => f.FUND_VIEW_TAB === path);
    fields.forEach((fieldConfig) => {
      if (erroredFields[fieldConfig.NAME]) {
        tabInfo.hasErrorField = true;
      }
    });
  });

  const disableAllFields =
    mode === FUNDS_VIEW_MODE.CREATE ? fundsStore.isCreatingFund : fundsStore.fundsInAction[fund.id];

  return (
    <>
      <div className="header-backlink">
        <div className="gray-title-panel">
          <div className="title-section">
            <h4 className={state.fields[FUND_FIELDS.NAME.NAME] ? '' : 'empty'}>
              {state.fields[FUND_FIELDS.NAME.NAME] || 'Fund name'}
            </h4>
          </div>
          <h6>Create and manage your fund profiles</h6>
          <div className="actions">
            <button
              className="btn btn-transparent btn-short disable-fade-transparent"
              disabled={state.isRestoreDisabled}
              onClick={state.restore}>
              Restore
            </button>
            <button
              className="btn btn-primary btn-short disable-fade-primary"
              disabled={state.isSaveDisabled}
              onClick={mode === FUNDS_VIEW_MODE.CREATE ? createFund : editFund}>
              Save
            </button>
          </div>
        </div>
        <Link to={paths.FUNDS} className="backlink" onClick={onBackClick}>
          Back to my funds
        </Link>
      </div>
      <div className="tabs">
        {tabConfig.map(({ path, desktopLabel, mobileLabel = '', id }, i) => {
          const isActive = state.tab === path;
          const isErrored = tabsInfo[i].hasErrorField;
          const tabClasNames = ['tab'];

          if (isActive) {
            tabClasNames.push('active');
          }

          if (isErrored && !isActive) {
            tabClasNames.push('error-tab');
          }

          return (
            <div
              key={path}
              id={id}
              className={tabClasNames.join(' ')}
              onClick={() => state.setTab(path, id)}>
              <div className="desktop-text">{desktopLabel}</div>
              <div className="mobile-text">{mobileLabel || desktopLabel}</div>
            </div>
          );
        })}
      </div>
      {state.tab === FUNDS_VIEW_TABS.INFORMATION && (
        <div className="form-body form-row-layout">
          <div className="row">
            <Input
              field={FUND_FIELDS.NAME}
              value={state.fields[FUND_FIELDS.NAME.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={true}
            />
            <Input
              field={FUND_FIELDS.VINTAGE}
              value={state.fields[FUND_FIELDS.VINTAGE.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
          <div className="row">
            <Input
              field={FUND_FIELDS.VOLUME}
              value={state.fields[FUND_FIELDS.VOLUME.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
          <div className="row">
            <TextArea
              field={FUND_FIELDS.DESCRIPTION}
              value={state.fields[FUND_FIELDS.DESCRIPTION.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={true}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === FUNDS_VIEW_TABS.GEO_INDUSTRIES && (
        <div className="form-body form-row-layout">
          <div className="row">
            <MultiSelect
              field={FUND_FIELDS.GEOGRAPHIES}
              value={state.fields[FUND_FIELDS.GEOGRAPHIES.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <MultiSelect
              field={FUND_FIELDS.INDUSTRIES}
              value={state.fields[FUND_FIELDS.INDUSTRIES.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === FUNDS_VIEW_TABS.FINANCIAL_METRICS && (
        <div className="form-body form-row-layout">
          <div className="row">
            <RangeInput
              fieldMin={FUND_FIELDS.REVENUE_RANGE_MIN}
              fieldMax={FUND_FIELDS.REVENUE_RANGE_MAX}
              valueMin={state.fields[FUND_FIELDS.REVENUE_RANGE_MIN.NAME]}
              valueMax={state.fields[FUND_FIELDS.REVENUE_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
          </div>
          <div className="row">
            <RangeInput
              fieldMin={FUND_FIELDS.EBITDA_RANGE_MIN}
              fieldMax={FUND_FIELDS.EBITDA_RANGE_MAX}
              valueMin={state.fields[FUND_FIELDS.EBITDA_RANGE_MIN.NAME]}
              valueMax={state.fields[FUND_FIELDS.EBITDA_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <Input
              field={FUND_FIELDS.EBITDA_MARGIN}
              value={state.fields[FUND_FIELDS.EBITDA_MARGIN.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
          <div className="row">
            <RangeInput
              fieldMin={FUND_FIELDS.EBIT_RANGE_MIN}
              fieldMax={FUND_FIELDS.EBIT_RANGE_MAX}
              valueMin={state.fields[FUND_FIELDS.EBIT_RANGE_MIN.NAME]}
              valueMax={state.fields[FUND_FIELDS.EBIT_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <Input
              field={FUND_FIELDS.EBIT_MARGIN}
              value={state.fields[FUND_FIELDS.EBIT_MARGIN.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === FUNDS_VIEW_TABS.TRANSACTION_TYPES && (
        <div className="form-body form-row-layout">
          <div className="row">
            <MultiSelect
              field={FUND_FIELDS.SITUATION}
              value={state.fields[FUND_FIELDS.SITUATION.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
            <MultiSelect
              field={FUND_FIELDS.EQUITY}
              value={state.fields[FUND_FIELDS.EQUITY.NAME]}
              setFieldValue={state.setFieldValue}
              disabled={disableAllFields}
              inputWrap={{ enable: true, className: 'col' }}
            />
          </div>
        </div>
      )}
      {state.tab === FUNDS_VIEW_TABS.VALUATION && (
        <div className="form-body form-row-layout">
          <div className="row">
            <RangeInput
              fieldMin={FUND_FIELDS.ENTERPRISE_RANGE_MIN}
              fieldMax={FUND_FIELDS.ENTERPRISE_RANGE_MAX}
              valueMin={state.fields[FUND_FIELDS.ENTERPRISE_RANGE_MIN.NAME]}
              valueMax={state.fields[FUND_FIELDS.ENTERPRISE_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
            <RangeInput
              fieldMin={FUND_FIELDS.EQUITY_RANGE_MIN}
              fieldMax={FUND_FIELDS.EQUITY_RANGE_MAX}
              valueMin={state.fields[FUND_FIELDS.EQUITY_RANGE_MIN.NAME]}
              valueMax={state.fields[FUND_FIELDS.EQUITY_RANGE_MAX.NAME]}
              disabled={disableAllFields}
              setFieldValue={state.setFieldValue}
              messages={state.validationFields.messages}
              showOnSubmitErrorState={state.onSubmitErrorState}
              wrapClass={'col'}
            />
          </div>
        </div>
      )}
    </>
  );
});

export default FundView;
